import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Popover } from "radix-ui";

import {
  CLASSES,
  DEFAULT_SIDEBAR,
  TOOL_TYPE,
  arrayToMap,
  capitalizeString,
  isShallowEqual,
} from "@excalidraw/common";

import { mutateElement } from "@excalidraw/element";

import { showSelectedShapeActions } from "@excalidraw/element";

import { ShapeCache } from "@excalidraw/element";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { actionToggleStats } from "../actions";
import { trackEvent } from "../analytics";
import { TunnelsContext, useInitializeTunnels } from "../context/tunnels";
import { UIAppStateContext } from "../context/ui-appState";
import { useAtom, useAtomValue } from "../editor-jotai";

import { t } from "../i18n";
import { getScrollToContentState } from "../scene";

import {
  SelectedShapeActions,
  ShapesSwitcher,
  CompactShapeActions,
} from "./Actions";
import { BrushPanel } from "./BrushPanel/BrushPanel";
import { LoadingMessage } from "./LoadingMessage";
import { MobileMenu } from "./MobileMenu";
import { PasteChartDialog } from "./PasteChartDialog";
import { Section } from "./Section";
import Stack from "./Stack";
import { UserList } from "./UserList";
import { PenModeButton } from "./PenModeButton";
import { PenPathActions } from "./pen/PenPathActions";
import Footer from "./footer/Footer";
import { isSidebarDockedAtom } from "./Sidebar/Sidebar";
import MainMenu from "./main-menu/MainMenu";
import { ActiveConfirmDialog } from "./ActiveConfirmDialog";
import {
  useEditorInterface,
  useStylesPanelMode,
  useExcalidrawContainer,
} from "./App";
import { OverwriteConfirmDialog } from "./OverwriteConfirm/OverwriteConfirm";
import {
  sidebarRightIcon,
  LibraryIcon,
  scrollBackToContentIcon,
} from "./icons";
import { Tooltip } from "./Tooltip";
import { ToolButton } from "./ToolButton";
import { PropertiesPopover } from "./PropertiesPopover";
import { LibraryMenu } from "./LibraryMenu";
import { DefaultSidebar } from "./DefaultSidebar";
import { TTDDialog } from "./TTDDialog/TTDDialog";
import { Stats } from "./Stats";
import ElementLinkDialog from "./ElementLinkDialog";
import { ErrorDialog } from "./ErrorDialog";
import { EyeDropper, activeEyeDropperAtom } from "./EyeDropper";
import { FixedSideContainer } from "./FixedSideContainer";
import { HelpDialog } from "./HelpDialog";
import { HintViewer } from "./HintViewer";
import { ImageExportDialog } from "./ImageExportDialog";
import { Island } from "./Island";
import { JSONExportDialog } from "./JSONExportDialog";
import { LaserPointerButton } from "./LaserPointerButton";
import { Toast } from "./Toast";

import "./LayerUI.scss";
import "./Toolbar.scss";

import type { ActionManager } from "../actions/manager";

import type { Language } from "../i18n";
import type {
  AppProps,
  AppState,
  ExcalidrawProps,
  BinaryFiles,
  UIAppState,
  AppClassProperties,
} from "../types";

interface LayerUIProps {
  actionManager: ActionManager;
  appState: UIAppState;
  files: BinaryFiles;
  canvas: HTMLCanvasElement;
  setAppState: React.Component<any, AppState>["setState"];
  elements: readonly NonDeletedExcalidrawElement[];
  onHandToolToggle: () => void;
  onPenModeToggle: AppClassProperties["togglePenMode"];
  showExitZenModeBtn: boolean;
  langCode: Language["code"];
  renderTopLeftUI?: ExcalidrawProps["renderTopLeftUI"];
  renderTopRightUI?: ExcalidrawProps["renderTopRightUI"];
  renderCustomStats?: ExcalidrawProps["renderCustomStats"];
  UIOptions: AppProps["UIOptions"];
  onExportImage: AppClassProperties["onExportImage"];
  renderWelcomeScreen: boolean;
  children?: React.ReactNode;
  app: AppClassProperties;
  isCollaborating: boolean;
  generateLinkForSelection?: AppProps["generateLinkForSelection"];
}

const DefaultMainMenu: React.FC<{
  UIOptions: AppProps["UIOptions"];
}> = ({ UIOptions }) => {
  return (
    <MainMenu __fallback>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      {/* FIXME we should to test for this inside the item itself */}
      {UIOptions.canvasActions.export && <MainMenu.DefaultItems.Export />}
      {/* FIXME we should to test for this inside the item itself */}
      {UIOptions.canvasActions.saveAsImage && (
        <MainMenu.DefaultItems.SaveAsImage />
      )}
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.Group title="Chartoon links">
        <MainMenu.DefaultItems.Socials />
      </MainMenu.Group>
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme={false} />
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
};

const DefaultOverwriteConfirmDialog = () => {
  return (
    <OverwriteConfirmDialog __fallback>
      <OverwriteConfirmDialog.Actions.SaveToDisk />
      <OverwriteConfirmDialog.Actions.ExportToImage />
    </OverwriteConfirmDialog>
  );
};

const LayerUI = ({
  actionManager,
  appState,
  files,
  setAppState,
  elements,
  canvas,
  onHandToolToggle,
  onPenModeToggle,
  showExitZenModeBtn,
  renderTopLeftUI,
  renderTopRightUI,
  renderCustomStats,
  UIOptions,
  onExportImage,
  renderWelcomeScreen,
  children,
  app,
  isCollaborating,
  generateLinkForSelection,
}: LayerUIProps) => {
  const editorInterface = useEditorInterface();
  const stylesPanelMode = useStylesPanelMode();
  const isCompactStylesPanel = stylesPanelMode === "compact";
  const tunnels = useInitializeTunnels();
  const { container } = useExcalidrawContainer();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  // The library popover is portaled into `container` (PropertiesPopover), so
  // it isn't a DOM descendant of the tool column and can't size itself off it
  // in pure CSS. Measure the column instead and publish both numbers: its
  // height as a custom property the popover's Island reads, and the
  // trigger→column-top delta as the popover's alignOffset, so the panel spans
  // exactly the same vertical extent as the toolbar. Callback ref (not
  // useRef) so the observer re-attaches if the column unmounts/remounts, e.g.
  // toggling view mode.
  const [toolbarColumn, setToolbarColumn] = useState<HTMLDivElement | null>(
    null,
  );
  // fallback matches the CSS fallback height below (480px popover top-aligned
  // against a ~439px column) for the first paint before the observer fires.
  const [libraryAlignOffset, setLibraryAlignOffset] = useState(-439);

  useEffect(() => {
    if (!toolbarColumn || !container || typeof ResizeObserver === "undefined") {
      return;
    }
    const measure = () => {
      const columnRect = toolbarColumn.getBoundingClientRect();
      // The tool column is content-sized and can itself overflow a short
      // viewport, so matching it verbatim would push the panel's scrollable
      // bottom off-screen. Clamp to the room actually left below its top edge
      // (min floor so the panel stays usable rather than collapsing).
      const available = window.innerHeight - columnRect.top - 8;
      const height = Math.max(240, Math.min(columnRect.height, available));
      container.style.setProperty(
        "--library-popover-height",
        `${Math.round(height)}px`,
      );
      const trigger = toolbarColumn.querySelector(
        '[data-testid="toolbar-library-trigger"]',
      );
      if (trigger) {
        const offset = Math.round(
          columnRect.top - trigger.getBoundingClientRect().top,
        );
        // the observer also fires for resizes that don't move the trigger
        // (e.g. width-only) — don't re-render LayerUI for those
        setLibraryAlignOffset((prev) => (prev === offset ? prev : offset));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(toolbarColumn);
    // the column's own box doesn't change when only the window does, so the
    // observer alone would never re-run the viewport clamp above
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      container.style.removeProperty("--library-popover-height");
    };
  }, [toolbarColumn, container]);

  const spacing = isCompactStylesPanel
    ? {
        menuTopGap: 4,
        toolbarColGap: 4,
        toolbarRowGap: 1,
        toolbarInnerRowGap: 0.5,
        // gap is unitless * --space-factor (0.25rem = 4px, theme.scss), so
        // 1.25 == 5px exactly — the two toolbar islands' fixed gap.
        toolbarGroupGap: 1.25,
        islandPadding: 1,
        collabMarginLeft: 8,
      }
    : {
        menuTopGap: 6,
        toolbarColGap: 4,
        toolbarRowGap: 1,
        toolbarInnerRowGap: 1,
        // see comment above — 1.25 * 4px == 5px exactly.
        toolbarGroupGap: 1.25,
        islandPadding: 1,
        collabMarginLeft: 8,
      };

  const TunnelsJotaiProvider = tunnels.tunnelsJotai.Provider;

  const [eyeDropperState, setEyeDropperState] = useAtom(activeEyeDropperAtom);

  const renderJSONExportDialog = () => {
    if (!UIOptions.canvasActions.export) {
      return null;
    }

    return (
      <JSONExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        exportOpts={UIOptions.canvasActions.export}
        canvas={canvas}
        setAppState={setAppState}
      />
    );
  };

  const renderImageExportDialog = () => {
    if (
      !UIOptions.canvasActions.saveAsImage ||
      appState.openDialog?.name !== "imageExport"
    ) {
      return null;
    }

    return (
      <ImageExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        onExportImage={onExportImage}
        onCloseRequest={() => setAppState({ openDialog: null })}
        name={app.getName()}
      />
    );
  };

  const renderCanvasActions = () => (
    <div style={{ position: "relative" }}>
      {/* wrapping to Fragment stops React from occasionally complaining
                about identical Keys */}
      <tunnels.MainMenuTunnel.Out />
      {renderWelcomeScreen && <tunnels.WelcomeScreenMenuHintTunnel.Out />}
    </div>
  );

  const renderSelectedShapeActions = () => {
    return (
      <Section
        heading="selectedShapeActions"
        className={clsx("selected-shape-actions zen-mode-transition", {
          "transition-left": appState.zenModeEnabled,
        })}
      >
        {isCompactStylesPanel ? (
          <Island
            className={clsx("compact-shape-actions-island")}
            padding={0}
            data-viewport-ui="side"
            data-viewport-ui-name="stylesPanel"
            style={{
              // we want to make sure this doesn't overflow so subtracting the
              // approximate height of hamburgerMenu + footer
              maxHeight: `${appState.height - 166}px`,
            }}
          >
            <CompactShapeActions
              appState={appState}
              elementsMap={app.scene.getNonDeletedElementsMap()}
              renderAction={actionManager.renderAction}
              app={app}
              setAppState={setAppState}
            />
          </Island>
        ) : (
          <Island
            className={CLASSES.SHAPE_ACTIONS_MENU}
            padding={2}
            style={{
              // we want to make sure this doesn't overflow so subtracting the
              // approximate height of hamburgerMenu + footer
              maxHeight: `${appState.height - 166}px`,
            }}
            data-viewport-ui="side"
            data-viewport-ui-name="stylesPanel"
          >
            <SelectedShapeActions
              appState={appState}
              elementsMap={app.scene.getNonDeletedElementsMap()}
              renderAction={actionManager.renderAction}
              app={app}
            />
          </Island>
        )}
      </Section>
    );
  };

  const renderFixedSideContainer = () => {
    const shouldRenderSelectedShapeActions = showSelectedShapeActions(
      appState,
      elements,
    );
    const shouldRenderBrushPanel =
      !appState.viewModeEnabled &&
      (appState.activeTool.type === "brush" ||
        appState.activeTool.type === "eraser");

    const shouldShowStats =
      appState.stats.open &&
      !appState.zenModeEnabled &&
      !appState.viewModeEnabled &&
      appState.openDialog?.name !== "elementLinkSelector";

    return (
      <FixedSideContainer side="left">
        <div className="App-menu App-menu_left">
          <Stack.Row
            gap={spacing.menuTopGap}
            className={clsx("App-menu_left__toolbar")}
          >
            {renderCanvasActions()}
          </Stack.Row>
          {/* shape properties panel — shown when elements/tool are selected.
              Left toolbar moved here (was top), so render it in the left menu. */}
          <div
            className={clsx("selected-shape-actions-container", {
              "selected-shape-actions-container--compact": isCompactStylesPanel,
              "selected-shape-actions-container--stacked":
                shouldRenderBrushPanel,
            })}
          >
            {/* the brush panel is self-contained (its own size/opacity/brush
                list) and replaces the generic shape-actions styling panel —
                don't render both, or the old panel shows through behind it */}
            {shouldRenderSelectedShapeActions &&
              !shouldRenderBrushPanel &&
              renderSelectedShapeActions()}
            {shouldRenderBrushPanel && (
              <BrushPanel
                appState={appState}
                setAppState={setAppState}
                mode={appState.activeTool.type === "brush" ? "brush" : "eraser"}
              />
            )}
          </div>
          {!appState.viewModeEnabled &&
            appState.openDialog?.name !== "elementLinkSelector" && (
              <Section heading="shapes" className="shapes-section">
                {(heading: React.ReactNode) => (
                  <div style={{ position: "relative" }}>
                    {renderWelcomeScreen && (
                      <tunnels.WelcomeScreenToolbarHintTunnel.Out />
                    )}
                    <Stack.Col gap={spacing.toolbarColGap} align="start">
                      <Stack.Row
                        gap={spacing.toolbarRowGap}
                        className={clsx("App-toolbar-container", {
                          "zen-mode": appState.zenModeEnabled,
                        })}
                      >
                        {/* two separate floating boxes — pointer tools
                            (hand + selection/lasso) and the rest — with a
                            real gap between them so the canvas shows
                            through instead of one continuous bar. */}
                        <Stack.Col
                          gap={spacing.toolbarGroupGap}
                          align="center"
                          ref={setToolbarColumn}
                        >
                          <Island
                            padding={spacing.islandPadding}
                            className={clsx("App-toolbar", {
                              "zen-mode": appState.zenModeEnabled,
                              "App-toolbar--compact": isCompactStylesPanel,
                            })}
                            data-viewport-ui="top"
                          >
                            <HintViewer
                              appState={appState}
                              isMobile={editorInterface.formFactor === "phone"}
                              editorInterface={editorInterface}
                              app={app}
                            />
                            {heading}
                            <Stack.Row gap={spacing.toolbarInnerRowGap}>
                              {/* in compact UI the pen mode button is rendered
                                  as a separate floating button below the compact
                                  actions menu */}
                              {!isCompactStylesPanel && (
                                <PenModeButton
                                  zenModeEnabled={appState.zenModeEnabled}
                                  checked={appState.penMode}
                                  onChange={() => onPenModeToggle(null)}
                                  title={t("toolBar.penMode")}
                                  penDetected={appState.penDetected}
                                />
                              )}

                              <ShapesSwitcher
                                group="pointer"
                                setAppState={setAppState}
                                activeTool={appState.activeTool}
                                UIOptions={UIOptions}
                                app={app}
                              />
                            </Stack.Row>
                          </Island>

                          <Island
                            padding={spacing.islandPadding}
                            className={clsx("App-toolbar", {
                              "zen-mode": appState.zenModeEnabled,
                              "App-toolbar--compact": isCompactStylesPanel,
                            })}
                          >
                            <Stack.Row gap={spacing.toolbarInnerRowGap}>
                              <ShapesSwitcher
                                group="tools"
                                setAppState={setAppState}
                                activeTool={appState.activeTool}
                                UIOptions={UIOptions}
                                app={app}
                              />
                            </Stack.Row>
                          </Island>

                          {/* personal library — its own island at the bottom
                              of the tool column, opening a floating panel
                              (the sidebar library tab was removed, see
                              DefaultSidebar.tsx). */}
                          <Popover.Root
                            open={isLibraryOpen}
                            onOpenChange={setIsLibraryOpen}
                          >
                            <Island
                              padding={spacing.islandPadding}
                              className={clsx("App-toolbar", {
                                "zen-mode": appState.zenModeEnabled,
                                "App-toolbar--compact": isCompactStylesPanel,
                              })}
                            >
                              <Popover.Trigger asChild>
                                <ToolButton
                                  type="button"
                                  data-testid="toolbar-library-trigger"
                                  icon={LibraryIcon}
                                  title={capitalizeString(t("toolBar.library"))}
                                  aria-label={capitalizeString(
                                    t("toolBar.library"),
                                  )}
                                  className={clsx({
                                    "App-toolbar__group-trigger--active":
                                      isLibraryOpen,
                                  })}
                                  onClick={(e) => {
                                    // matches the shapes-group trigger in
                                    // Actions.tsx — stop Radix's own toggle
                                    // from cancelling this handler out.
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsLibraryOpen((open) => !open);
                                  }}
                                />
                              </Popover.Trigger>
                            </Island>
                            {isLibraryOpen && (
                              <PropertiesPopover
                                container={container}
                                onClose={() => setIsLibraryOpen(false)}
                                className="App-toolbar__library-popover"
                                side="right"
                                align="start"
                                // library trigger sits at the bottom of the
                                // tool column; align="start" would top-align
                                // the panel with it. Shift up by the measured
                                // trigger→column-top distance so the panel's
                                // top lines up with the top of the toolbar
                                // instead (was a hardcoded -439px).
                                alignOffset={libraryAlignOffset}
                                // brush-panel parity: that panel sits at
                                // left: 3.5rem, i.e. ~8px clear of the tool
                                // column. Radix measures from the trigger
                                // button, which is inset ~5px inside the
                                // column's Island — hence 13, not 8.
                                // ponytail: two literals (this and the 3.5rem)
                                // instead of a shared token; hoist if a third
                                // panel needs the same alignment.
                                sideOffset={13}
                                hideArrow
                              >
                                <LibraryMenu />
                              </PropertiesPopover>
                            )}
                          </Popover.Root>
                        </Stack.Col>
                        {isCollaborating && (
                          <Island
                            style={{
                              marginLeft: spacing.collabMarginLeft,
                              alignSelf: "center",
                              height: "fit-content",
                            }}
                          >
                            <LaserPointerButton
                              title={t("toolBar.laser")}
                              checked={
                                appState.activeTool.type === TOOL_TYPE.laser
                              }
                              onChange={() =>
                                app.setActiveTool({ type: TOOL_TYPE.laser })
                              }
                              isMobile
                            />
                          </Island>
                        )}
                      </Stack.Row>
                    </Stack.Col>
                  </div>
                )}
              </Section>
            )}
          <div
            className={clsx(
              "layer-ui__wrapper__top-right zen-mode-transition",
              {
                "transition-right": appState.zenModeEnabled,
                "layer-ui__wrapper__top-right--compact": isCompactStylesPanel,
              },
            )}
          >
            {appState.collaborators.size > 0 && (
              <UserList
                collaborators={appState.collaborators}
                userToFollow={appState.userToFollow?.socketId || null}
              />
            )}
            {renderTopRightUI?.(
              editorInterface.formFactor === "phone",
              appState,
            )}
            {!appState.viewModeEnabled &&
              appState.openDialog?.name !== "elementLinkSelector" &&
              // hide button when sidebar docked
              (!isSidebarDocked ||
                appState.openSidebar?.name !== DEFAULT_SIDEBAR.name) && (
                <tunnels.DefaultSidebarTriggerTunnel.Out />
              )}
            {shouldShowStats && (
              <Stats
                app={app}
                onClose={() => {
                  actionManager.executeAction(actionToggleStats);
                }}
                renderCustomStats={renderCustomStats}
              />
            )}
          </div>
        </div>
      </FixedSideContainer>
    );
  };

  const renderSidebars = () => {
    return (
      <DefaultSidebar
        __fallback
        onDock={(docked) => {
          trackEvent(
            "sidebar",
            `toggleDock (${docked ? "dock" : "undock"})`,
            `(${
              editorInterface.formFactor === "phone" ? "mobile" : "desktop"
            })`,
          );
        }}
      />
    );
  };

  const isSidebarDocked = useAtomValue(isSidebarDockedAtom);

  const layerUIJSX = (
    <>
      {/* ------------------------- tunneled UI ---------------------------- */}
      {/* make sure we render host app components first so that we can detect
          them first on initial render to optimize layout shift */}
      {children}
      {/* render component fallbacks. Can be rendered anywhere as they'll be
          tunneled away. We only render tunneled components that actually
        have defaults when host do not render anything. */}
      <DefaultMainMenu UIOptions={UIOptions} />
      <DefaultSidebar.Trigger
        __fallback
        icon={sidebarRightIcon}
        // the library tab was pulled out of this sidebar (now a toolbar
        // popover, see the library Island above); this trigger just
        // toggles the side panel open to its first (Properties) tab.
        title="Properties"
        onToggle={(open) => {
          if (open) {
            trackEvent(
              "sidebar",
              `${DEFAULT_SIDEBAR.name} (open)`,
              `button (${
                editorInterface.formFactor === "phone" ? "mobile" : "desktop"
              })`,
            );
          }
        }}
        tab="properties"
      />
      <DefaultOverwriteConfirmDialog />
      {appState.openDialog?.name === "ttd" && <TTDDialog __fallback />}
      {/* ------------------------------------------------------------------ */}

      {appState.isLoading && <LoadingMessage delay={250} />}
      {appState.errorMessage && (
        <ErrorDialog onClose={() => setAppState({ errorMessage: null })}>
          {appState.errorMessage}
        </ErrorDialog>
      )}
      {eyeDropperState && editorInterface.formFactor !== "phone" && (
        <EyeDropper
          colorPickerType={eyeDropperState.colorPickerType}
          onCancel={() => {
            setEyeDropperState(null);
          }}
          onChange={(colorPickerType, color, selectedElements, { altKey }) => {
            if (
              colorPickerType !== "elementBackground" &&
              colorPickerType !== "elementStroke"
            ) {
              return;
            }

            if (selectedElements.length) {
              for (const element of selectedElements) {
                mutateElement(element, arrayToMap(elements), {
                  [altKey && eyeDropperState.swapPreviewOnAlt
                    ? colorPickerType === "elementBackground"
                      ? "strokeColor"
                      : "backgroundColor"
                    : colorPickerType === "elementBackground"
                    ? "backgroundColor"
                    : "strokeColor"]: color,
                });
                ShapeCache.delete(element);
              }
              app.scene.triggerUpdate();
            } else if (colorPickerType === "elementBackground") {
              setAppState({
                currentItemBackgroundColor: color,
              });
            } else {
              setAppState({ currentItemStrokeColor: color });
            }
          }}
          onSelect={(color, event) => {
            setEyeDropperState((state) => {
              return state?.keepOpenOnAlt && event.altKey ? state : null;
            });
            eyeDropperState?.onSelect?.(color, event);
          }}
        />
      )}
      {appState.openDialog?.name === "help" && (
        <HelpDialog
          onClose={() => {
            setAppState({ openDialog: null });
          }}
        />
      )}
      <ActiveConfirmDialog />
      <PenPathActions app={app} appState={appState} />
      {appState.openDialog?.name === "elementLinkSelector" && (
        <ElementLinkDialog
          sourceElementId={appState.openDialog.sourceElementId}
          onClose={() => {
            setAppState({
              openDialog: null,
            });
          }}
          scene={app.scene}
          appState={appState}
          generateLinkForSelection={generateLinkForSelection}
        />
      )}
      <tunnels.OverwriteConfirmDialogTunnel.Out />
      {renderImageExportDialog()}
      {renderJSONExportDialog()}
      {appState.openDialog?.name === "charts" && (
        <PasteChartDialog
          data={appState.openDialog.data}
          rawText={appState.openDialog.rawText}
          onClose={() =>
            setAppState({
              openDialog: null,
            })
          }
        />
      )}
      {editorInterface.formFactor === "phone" && (
        <MobileMenu
          app={app}
          appState={appState}
          elements={elements}
          actionManager={actionManager}
          renderJSONExportDialog={renderJSONExportDialog}
          renderImageExportDialog={renderImageExportDialog}
          setAppState={setAppState}
          onHandToolToggle={onHandToolToggle}
          onPenModeToggle={onPenModeToggle}
          renderTopLeftUI={renderTopLeftUI}
          renderTopRightUI={renderTopRightUI}
          renderSidebars={renderSidebars}
          renderWelcomeScreen={renderWelcomeScreen}
          UIOptions={UIOptions}
        />
      )}
      {editorInterface.formFactor !== "phone" && (
        <>
          <div
            className="layer-ui__wrapper"
            style={
              appState.openSidebar &&
              isSidebarDocked &&
              editorInterface.canFitSidebar
                ? { width: `calc(100% - var(--right-sidebar-width))` }
                : {}
            }
          >
            {renderWelcomeScreen && <tunnels.WelcomeScreenCenterTunnel.Out />}
            {renderFixedSideContainer()}
            <Footer
              appState={appState}
              actionManager={actionManager}
              showExitZenModeBtn={showExitZenModeBtn}
              renderWelcomeScreen={renderWelcomeScreen}
            />
            {(appState.toast || appState.scrolledOutside) && (
              <div className="floating-status-stack">
                {appState.toast && (
                  <Toast
                    message={appState.toast.message}
                    onClose={() => setAppState({ toast: null })}
                    duration={appState.toast.duration}
                    closable={appState.toast.closable}
                  />
                )}
                {!appState.toast && appState.scrolledOutside && (
                  // icon-only, with the label in a tooltip — it sits over the
                  // canvas, so it reads as one of the floating controls (the
                  // zoom stepper) rather than a banner
                  <Tooltip label={t("buttons.scrollBackToContent")}>
                    <button
                      type="button"
                      className="scroll-back-to-content"
                      aria-label={t("buttons.scrollBackToContent")}
                      onClick={() => {
                        setAppState((appState) => ({
                          ...getScrollToContentState(elements, appState),
                        }));
                      }}
                    >
                      {scrollBackToContentIcon}
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          {renderSidebars()}
        </>
      )}
    </>
  );

  return (
    <UIAppStateContext.Provider value={appState}>
      <TunnelsJotaiProvider>
        <TunnelsContext.Provider value={tunnels}>
          {layerUIJSX}
        </TunnelsContext.Provider>
      </TunnelsJotaiProvider>
    </UIAppStateContext.Provider>
  );
};

const stripIrrelevantAppStateProps = (appState: AppState): UIAppState => {
  const {
    cursorButton,
    scrollX,
    scrollY,
    zoom,
    shouldCacheIgnoreZoom,
    snapLines,
    originSnapOffset,
    suggestedBinding,
    frameToHighlight,
    elementsToHighlight,
    ...ret
  } = appState;
  return ret;
};

const areEqual = (prevProps: LayerUIProps, nextProps: LayerUIProps) => {
  // short-circuit early
  if (prevProps.children !== nextProps.children) {
    return false;
  }

  const { canvas: _pC, appState: prevAppState, ...prev } = prevProps;
  const { canvas: _nC, appState: nextAppState, ...next } = nextProps;

  return (
    isShallowEqual(
      // asserting AppState because we're being passed the whole AppState
      // but resolve to only the UI-relevant props
      stripIrrelevantAppStateProps(prevAppState as AppState),
      stripIrrelevantAppStateProps(nextAppState as AppState),
      {
        selectedElementIds: isShallowEqual,
        selectedGroupIds: isShallowEqual,
      },
    ) && isShallowEqual(prev, next)
  );
};

export default React.memo(LayerUI, areEqual);
